import { Config3D } from "../../../Config3D";
import { LayaGL } from "../../layagl/LayaGL";
import { Matrix4x4 } from "../../maths/Matrix4x4";
import { BaseRenderType } from "../../RenderDriver/RenderModuleData/Design/3D/I3DRenderModuleData";
import { RenderCapable } from "../../RenderEngine/RenderEnum/RenderCapable";
import { Shader3D } from "../../RenderEngine/RenderShader/Shader3D";
import { VertexMesh } from "../../RenderEngine/RenderShader/VertexMesh";
import { Laya3DRender } from "../RenderObjs/Laya3DRender";
import { MeshUtil } from "../resource/models/MeshUtil";
import { MeshFilter } from "./MeshFilter";
import { MeshSprite3DShaderDeclaration } from "./MeshSprite3DShaderDeclaration";
import { RenderableSprite3D } from "./RenderableSprite3D";
import { BlinnPhongMaterial } from "./material/BlinnPhongMaterial";
import { BaseRender } from "./render/BaseRender";
import { SubMeshRenderElement } from "./render/SubMeshRenderElement";
import { Stat } from "../../utils/Stat";
import { StatElement } from "../../layagl/StatisticsContext";
export class MeshRenderer extends BaseRender {
    static __init__() {
        MeshSprite3DShaderDeclaration.SHADERDEFINE_UV0 = Shader3D.getDefineByName("UV");
        MeshSprite3DShaderDeclaration.SHADERDEFINE_COLOR = Shader3D.getDefineByName("COLOR");
        MeshSprite3DShaderDeclaration.SHADERDEFINE_UV1 = Shader3D.getDefineByName("UV1");
        MeshSprite3DShaderDeclaration.SHADERDEFINE_TANGENT = Shader3D.getDefineByName("TANGENT");
        MeshSprite3DShaderDeclaration.SHADERDEFINE_BONEW = Shader3D.getDefineByName("VBONEW");
        MeshSprite3DShaderDeclaration.SHADERDEFINE_BONEI = Shader3D.getDefineByName("VBONEI");
        MeshSprite3DShaderDeclaration.SHADERDEFINE_GPU_INSTANCE = Shader3D.getDefineByName("GPU_INSTANCE");
    }
    constructor() {
        super();
        this._revertStaticBatchDefineUV1 = false;
        this.morphTargetActiveCount = 0;
        this._morphWeightChange = true;
        this._morphTargetValues = {};
        this._meshChange = false;
        this._projectionViewWorldMatrix = new Matrix4x4();
        this._baseRenderNode.renderNodeType = BaseRenderType.MeshRender;
    }
    _createBaseRenderNode() {
        return Laya3DRender.Render3DModuleDataFactory.createMeshRenderNode();
    }
    _createRenderElement() {
        return new SubMeshRenderElement();
    }
    getMesh() {
        return this._mesh;
    }
    _onEnable() {
        super._onEnable();
        const filter = this.owner.getComponent(MeshFilter);
        if (filter)
            filter._enabled && this._onMeshChange(filter.sharedMesh);
    }
    _getMeshDefine(mesh, out) {
        let define;
        out.length = 0;
        MeshUtil.getMeshDefine(mesh, out);
        return define;
    }
    _changeVertexDefine(mesh) {
        var defineDatas = this._baseRenderNode.shaderData;
        var lastValue = this._mesh;
        if (lastValue) {
            this._getMeshDefine(lastValue, MeshFilter._meshVerticeDefine);
            for (var i = 0, n = MeshFilter._meshVerticeDefine.length; i < n; i++)
                defineDatas.removeDefine(MeshFilter._meshVerticeDefine[i]);
        }
        if (mesh) {
            this._getMeshDefine(mesh, MeshFilter._meshVerticeDefine);
            for (var i = 0, n = MeshFilter._meshVerticeDefine.length; i < n; i++)
                defineDatas.addDefine(MeshFilter._meshVerticeDefine[i]);
        }
    }
    get morphTargetValues() {
        return this._morphTargetValues;
    }
    set morphTargetValues(value) {
        this._morphTargetValues = value;
    }
    _changeMorphTargetValue(key) {
        this._morphWeightChange = true;
    }
    setMorphChannelWeight(channelName, weight) {
        let mesh = this._mesh;
        if (mesh && mesh.morphTargetData) {
            let morphData = mesh.morphTargetData;
            let channel = morphData.getMorphChannel(channelName);
            this.morphTargetValues[channel.name] = weight;
            this._morphWeightChange = true;
        }
    }
    _applyMorphdata() {
        let mesh = this._mesh;
        let shaderData = this._baseRenderNode.shaderData;
        if (this._morphWeightChange && mesh) {
            let morphData = mesh.morphTargetData;
            let channelCount = morphData.channelCount;
            for (let channelIndex = 0; channelIndex < channelCount; channelIndex++) {
                let channel = morphData.getMorphChannelbyIndex(channelIndex);
                let weight = this.morphTargetValues[channel.name];
                let lastFullWeight = 0;
                channel.targets.forEach(target => {
                    if (weight <= target.fullWeight) {
                        this.morphTargetWeight[target._index] = (weight - lastFullWeight) / (target.fullWeight - lastFullWeight);
                    }
                    else {
                        this.morphTargetWeight[target._index] = 1;
                    }
                    lastFullWeight = target.fullWeight;
                });
            }
            let activeIndex = 0;
            this.morphTargetWeight.forEach((weight, index) => {
                if (weight > 0) {
                    let offset = activeIndex * 4;
                    this.morphTargetActiveData[offset] = index;
                    this.morphTargetActiveData[offset + 1] = weight;
                    activeIndex++;
                }
            });
            this.morphTargetActiveCount = Math.min(activeIndex, Config3D.maxMorphTargetCount);
            if (LayaGL.renderEngine.getCapable(RenderCapable.Texture3D)) {
                shaderData.setInt(RenderableSprite3D.MorphActiveCount, this.morphTargetActiveCount);
                shaderData.setBuffer(RenderableSprite3D.MorphActiceTargets, this.morphTargetActiveData);
            }
            else {
            }
            this._morphWeightChange = false;
        }
    }
    _setBelongScene(scene) {
        super._setBelongScene(scene);
    }
    _setUnBelongScene() {
        super._setUnBelongScene();
    }
    _statAdd() {
        super._statAdd();
        LayaGL.statAgent.recordCountData(StatElement.C_MeshRenderCount, 1);
    }
    _statRemove() {
        super._statRemove();
        LayaGL.statAgent.recordCountData(StatElement.C_MeshRenderCount, -1);
    }
    _changeMorphData(mesh) {
        let shaderData = this._baseRenderNode.shaderData;
        let oldMesh = this._mesh;
        const maxMorphTargetCount = Config3D.maxMorphTargetCount;
        let maxCount = maxMorphTargetCount;
        this.morphTargetActiveData = new Float32Array(maxCount * 4);
        if (LayaGL.renderEngine.getCapable(RenderCapable.Texture3D)) {
            if (oldMesh && oldMesh.morphTargetData) {
                let morphData = oldMesh.morphTargetData;
                shaderData.removeDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET);
                let morphVertexDec = morphData.vertexDec;
                morphVertexDec._vertexElements.forEach(element => {
                    switch (element.elementUsage) {
                        case VertexMesh.MESH_POSITION0:
                            shaderData.removeDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET_POSITION);
                            break;
                        case VertexMesh.MESH_NORMAL0:
                            shaderData.removeDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET_NORMAL);
                            break;
                        case VertexMesh.MESH_TANGENT0:
                            shaderData.removeDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET_TANGENT);
                            break;
                        default:
                            break;
                    }
                });
            }
            if (mesh && mesh.morphTargetData) {
                let morphData = mesh.morphTargetData;
                shaderData.addDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET);
                let morphVertexDec = morphData.vertexDec;
                morphVertexDec._vertexElements.forEach(element => {
                    switch (element.elementUsage) {
                        case VertexMesh.MESH_POSITION0:
                            shaderData.addDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET_POSITION);
                            break;
                        case VertexMesh.MESH_NORMAL0:
                            shaderData.addDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET_NORMAL);
                            break;
                        case VertexMesh.MESH_TANGENT0:
                            shaderData.addDefine(RenderableSprite3D.SHADERDEFINE_MORPHTARGET_TANGENT);
                            break;
                        default:
                            break;
                    }
                });
                shaderData.setVector(RenderableSprite3D.MorphAttriOffset, mesh.morphTargetData.attributeOffset);
                shaderData.setTexture(RenderableSprite3D.MorphTex, mesh.morphTargetData.targetTexture);
                shaderData.setVector(RenderableSprite3D.MorphParams, morphData.params);
                shaderData.setBuffer(RenderableSprite3D.MorphActiceTargets, this.morphTargetActiveData);
            }
        }
        if (oldMesh && oldMesh.morphTargetData) {
            this.morphTargetWeight = null;
            this.morphtargetChannels = null;
            this._morphTargetValues = {};
        }
        if (mesh && mesh.morphTargetData && LayaGL.renderEngine.getCapable(RenderCapable.Texture3D)) {
            let morphData = mesh.morphTargetData;
            let channelCount = morphData.channelCount;
            this.morphTargetWeight = new Float32Array(morphData.targetCount);
            this.morphtargetChannels = new Array(channelCount);
            for (let index = 0; index < channelCount; index++) {
                let channel = morphData.getMorphChannelbyIndex(index);
                this.morphtargetChannels[index] = channel;
                this._morphTargetValues[channel.name] = 0;
            }
            let additionShaderData = this._baseRenderNode.additionShaderData;
            additionShaderData.set("MorphTarget", shaderData);
            this._baseRenderNode.additionShaderData = additionShaderData;
        }
        else {
            let additionShaderData = this._baseRenderNode.additionShaderData;
            additionShaderData.delete("MorphTarget");
            this._baseRenderNode.additionShaderData = additionShaderData;
        }
    }
    _onMeshChange(mesh) {
        if (mesh && this._mesh != mesh) {
            this._changeVertexDefine(mesh);
            this._changeMorphData(mesh);
            this._mesh = mesh;
            if (mesh.morphTargetData)
                this.geometryBounds = mesh.morphTargetData.bounds;
            else
                this.geometryBounds = mesh.bounds;
            var count = mesh.subMeshCount;
            this._renderElements.length = count;
            let materials = this.sharedMaterials;
            materials.length = count;
            for (var i = 0; i < count; i++) {
                var renderElement = this._renderElements[i];
                if (!renderElement) {
                    renderElement = this._renderElements[i] = this._renderElements[i] ? this._renderElements[i] : this._createRenderElement();
                    this.owner && renderElement.setTransform(this.owner._transform);
                    renderElement.render = this;
                }
                materials[i] = materials[i] || BlinnPhongMaterial.defaultMaterial;
                renderElement.setGeometry(mesh.getSubMesh(i));
            }
            this.sharedMaterials = materials;
            this.boundsChange = true;
        }
        else if (!mesh) {
            this._renderElements.forEach;
            this._renderElements.forEach(element => {
                element._renderElementOBJ.destroy();
                element.destroy();
            });
            this._renderElements.length = 0;
            this._mesh = null;
            this._changeVertexDefine(null);
            this._changeMorphData(null);
            this.boundsChange = false;
        }
        this._setRenderElements();
        this._meshChange = true;
    }
    _onWorldMatNeedChange(flag) {
        super._onWorldMatNeedChange(flag);
        if (!this._mesh) {
            this.boundsChange = false;
        }
        this._baseRenderNode.ismoved.setValue(Stat.loopCount, LayaGL.renderEngine._framePassCount);
        this._baseRenderNode.ismoved = this._baseRenderNode.ismoved;
    }
    renderUpdate(context) {
        if (!this._mesh) {
            return;
        }
        if (LayaGL.renderEngine.getCapable(RenderCapable.Texture3D) && this._mesh.morphTargetData) {
            this._applyMorphdata();
        }
        if (!this._meshChange) {
            return;
        }
        if (this._renderElements.length == 1) {
            this._renderElements[0]._renderElementOBJ.isRender = this._renderElements[0]._geometry._prepareRender(context);
            this._renderElements[0]._geometry._updateRenderParams(context);
            let material = this.sharedMaterial;
            this._renderElements[0].material = material;
        }
        else {
            for (var i = 0, n = this._renderElements.length; i < n; i++) {
                this._renderElements[i]._renderElementOBJ.isRender = this._renderElements[i]._geometry._prepareRender(context);
                this._renderElements[i]._geometry._updateRenderParams(context);
                let material = this.sharedMaterials[i];
                this._renderElements[i].material = material;
            }
        }
        this._meshChange = false;
    }
    _onDestroy() {
        super._onDestroy();
        this._morphTargetValues = null;
    }
    _cloneTo(dest) {
        super._cloneTo(dest);
        dest._onMeshChange(this._mesh);
        if (this.morphTargetWeight) {
            dest.morphTargetWeight = new Float32Array(this.morphTargetWeight);
        }
        for (const key in this._morphTargetValues) {
            dest._morphTargetValues[key] = this._morphTargetValues[key];
        }
    }
}
